import type { CreateOrganization, Organization } from '@monorepo/shared'
import type {
  OrganizationSize,
  OrganizationStatus,
} from '@monorepo/shared/schemas/common'
import {
  ORGANIZATION_SIZES,
  ORGANIZATION_STATUS,
  ORGANIZATION_STATUSES,
} from '@monorepo/shared/schemas/common'
import { useNavigate } from '@tanstack/react-router'
import { Loader2, Percent, Save, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { TaxAssignmentDialog } from '@/shared/components/taxes'
import { Button } from '@/shared/components/ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Textarea } from '@/shared/components/ui/textarea'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import {
  useCreateOrganization,
  useUpdateOrganization,
} from '../hooks/useOrganizations'

interface OrganizationDrawerProps {
  organization?: Organization // undefined = CREATE mode, defined = EDIT mode
  open: boolean
  onOpenChange: (open: boolean) => void
  onOrganizationCreated?: (organization: Organization) => void
}

// Form type with required defaults
interface OrganizationFormData {
  name: string
  legal_name: string
  external_id: string
  description: string
  website: string
  industry: string
  size: OrganizationSize | ''
  employee_count: string
  email: string
  phone: string
  address_line1: string
  address_line2: string
  city: string
  state: string
  postal_code: string
  country: string
  tax_id: string
  currency: string
  timezone: string
  default_payment_terms: string
  status: OrganizationStatus
  metadata: Record<string, unknown>
}

export function OrganizationDrawer({
  organization,
  open,
  onOpenChange,
  onOrganizationCreated,
}: OrganizationDrawerProps) {
  const _navigate = useNavigate()
  const [taxDialogOpen, setTaxDialogOpen] = useState(false)

  const createOrganizationMutation = useCreateOrganization()
  const updateOrganizationMutation = useUpdateOrganization()

  const form = useForm<OrganizationFormData>({
    defaultValues: {
      name: '',
      legal_name: '',
      external_id: '',
      description: '',
      website: '',
      industry: '',
      size: '',
      employee_count: '',
      email: '',
      phone: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
      tax_id: '',
      currency: 'USD',
      timezone: 'UTC',
      default_payment_terms: '30',
      status: ORGANIZATION_STATUS.active,
      metadata: {},
    },
  })

  // Update form data when organization changes or reset when creating
  useEffect(() => {
    if (organization && open) {
      // Edit mode: populate with organization data
      form.reset({
        name: organization.name,
        legal_name: organization.legal_name || '',
        external_id: organization.external_id || '',
        description: organization.description || '',
        website: organization.website || '',
        industry: organization.industry || '',
        size: organization.size || '',
        employee_count: organization.employee_count?.toString() || '',
        email: organization.email || '',
        phone: organization.phone || '',
        address_line1: organization.address_line1 || '',
        address_line2: organization.address_line2 || '',
        city: organization.city || '',
        state: organization.state || '',
        postal_code: organization.postal_code || '',
        country: organization.country || '',
        tax_id: organization.tax_id || '',
        currency: organization.currency || 'USD',
        timezone: organization.timezone || 'UTC',
        default_payment_terms:
          organization.default_payment_terms?.toString() || '30',
        status: organization.status,
        metadata: organization.metadata || {},
      })
    } else if (!organization && open) {
      // Create mode: reset to default values
      form.reset({
        name: '',
        legal_name: '',
        external_id: '',
        description: '',
        website: '',
        industry: '',
        size: '',
        employee_count: '',
        email: '',
        phone: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        tax_id: '',
        currency: 'USD',
        timezone: 'UTC',
        default_payment_terms: '30',
        status: ORGANIZATION_STATUS.active,
        metadata: {},
      })
    }
  }, [organization, open, form])

  const onSubmit = async (data: OrganizationFormData) => {
    try {
      // Convert form data to CreateOrganization format
      const organizationData: CreateOrganization = {
        name: data.name,
        legal_name: data.legal_name || undefined,
        external_id: data.external_id || undefined,
        description: data.description || undefined,
        website: data.website || undefined,
        industry: data.industry || undefined,
        size: data.size || undefined,
        employee_count: data.employee_count
          ? parseInt(data.employee_count, 10)
          : undefined,
        email: data.email || undefined,
        phone: data.phone || undefined,
        address_line1: data.address_line1 || undefined,
        address_line2: data.address_line2 || undefined,
        city: data.city || undefined,
        state: data.state || undefined,
        postal_code: data.postal_code || undefined,
        country: data.country || undefined,
        tax_id: data.tax_id || undefined,
        currency: data.currency || 'USD',
        timezone: data.timezone || 'UTC',
        default_payment_terms: parseInt(data.default_payment_terms, 10) || 30,
        status: data.status,
        metadata: data.metadata,
      }

      if (organization) {
        // Edit mode: update existing organization
        const updatedOrganization =
          await updateOrganizationMutation.mutateAsync({
            id: organization.id,
            data: organizationData,
          })

        // If this is being used in nested mode (has callback), call it with the updated org
        if (onOrganizationCreated) {
          onOrganizationCreated(updatedOrganization)
        }
        onOpenChange(false)
      } else {
        // Create mode: create new organization
        const newOrganization =
          await createOrganizationMutation.mutateAsync(organizationData)

        // If this is being used in nested mode (has callback), call it with the created org
        if (onOrganizationCreated) {
          onOrganizationCreated(newOrganization)
        }
        // Close drawer after successful creation
        onOpenChange(false)
      }
    } catch (error) {
      toast.error(extractErrorMessage(error))
    }
  }

  const handleCancel = () => {
    if (organization) {
      // Reset form to original values
      form.reset()
    }
    onOpenChange(false)
  }

  const isEditMode = !!organization
  const isSaving =
    createOrganizationMutation.isPending || updateOrganizationMutation.isPending

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange} direction='right'>
        <DrawerContent className='h-full w-[50vw] min-w-[600px] max-w-[800px] ml-auto fixed right-0'>
          <DrawerHeader>
            <DrawerTitle>
              {isEditMode ? 'Edit Organization' : 'Create Organization'}
            </DrawerTitle>
            <DrawerDescription>
              {isEditMode
                ? 'Update organization information and settings'
                : 'Add a new organization to your system'}
            </DrawerDescription>
          </DrawerHeader>

          <div className='flex-1 overflow-y-auto px-4 pb-6'>
            <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
              {/* Basic Information */}
              <div className='space-y-4'>
                <h3 className='text-sm font-medium'>Basic Information</h3>

                <div className='space-y-2'>
                  <Label htmlFor='name'>Name *</Label>
                  <Input
                    id='name'
                    {...form.register('name', { required: true })}
                    placeholder='Organization name'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='legal_name'>Legal Name</Label>
                  <Input
                    id='legal_name'
                    {...form.register('legal_name')}
                    placeholder='Legal business name'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='external_id'>External ID</Label>
                  <Input
                    id='external_id'
                    {...form.register('external_id')}
                    placeholder='External system identifier'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='description'>Description</Label>
                  <Textarea
                    id='description'
                    {...form.register('description')}
                    placeholder='Organization description'
                    rows={3}
                  />
                </div>
              </div>

              {/* Company Details */}
              <div className='space-y-4'>
                <h3 className='text-sm font-medium'>Company Details</h3>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='industry'>Industry</Label>
                    <Input
                      id='industry'
                      {...form.register('industry')}
                      placeholder='e.g. Technology, Healthcare'
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='size'>Company Size</Label>
                    <Select
                      value={form.watch('size') || undefined}
                      onValueChange={value =>
                        form.setValue('size', value as OrganizationSize)
                      }
                    >
                      <SelectTrigger id='size'>
                        <SelectValue placeholder='Select size (optional)' />
                      </SelectTrigger>
                      <SelectContent>
                        {ORGANIZATION_SIZES.map(size => (
                          <SelectItem key={size} value={size}>
                            {size.charAt(0).toUpperCase() + size.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='employee_count'>Employee Count</Label>
                    <Input
                      id='employee_count'
                      type='number'
                      {...form.register('employee_count')}
                      placeholder='Number of employees'
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='website'>Website</Label>
                    <Input
                      id='website'
                      type='url'
                      {...form.register('website')}
                      placeholder='https://example.com'
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className='space-y-4'>
                <h3 className='text-sm font-medium'>Contact Information</h3>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='email'>Email</Label>
                    <Input
                      id='email'
                      type='email'
                      {...form.register('email')}
                      placeholder='contact@example.com'
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='phone'>Phone</Label>
                    <Input
                      id='phone'
                      {...form.register('phone')}
                      placeholder='+1234567890'
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className='space-y-4'>
                <h3 className='text-sm font-medium'>Address</h3>

                <div className='space-y-2'>
                  <Label htmlFor='address_line1'>Address Line 1</Label>
                  <Input
                    id='address_line1'
                    {...form.register('address_line1')}
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='address_line2'>Address Line 2</Label>
                  <Input
                    id='address_line2'
                    {...form.register('address_line2')}
                  />
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='city'>City</Label>
                    <Input id='city' {...form.register('city')} />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='state'>State/Province</Label>
                    <Input id='state' {...form.register('state')} />
                  </div>
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='postal_code'>Postal Code</Label>
                    <Input id='postal_code' {...form.register('postal_code')} />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='country'>Country</Label>
                    <Input
                      id='country'
                      {...form.register('country')}
                      placeholder='US'
                    />
                  </div>
                </div>
              </div>

              {/* Business Settings */}
              <div className='space-y-4'>
                <h3 className='text-sm font-medium'>Business Settings</h3>

                <div className='space-y-2'>
                  <Label htmlFor='tax_id'>Tax ID / VAT Number</Label>
                  <Input
                    id='tax_id'
                    {...form.register('tax_id')}
                    placeholder='Tax identification number'
                  />
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='currency'>Currency</Label>
                    <Input
                      id='currency'
                      {...form.register('currency')}
                      placeholder='USD'
                      maxLength={3}
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='timezone'>Timezone</Label>
                    <Input
                      id='timezone'
                      {...form.register('timezone')}
                      placeholder='UTC'
                    />
                  </div>
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='default_payment_terms'>
                      Default Payment Terms (days)
                    </Label>
                    <Input
                      id='default_payment_terms'
                      type='number'
                      {...form.register('default_payment_terms')}
                      placeholder='30'
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='status'>Status</Label>
                    <Select
                      value={form.watch('status')}
                      onValueChange={value =>
                        form.setValue('status', value as OrganizationStatus)
                      }
                    >
                      <SelectTrigger id='status'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ORGANIZATION_STATUSES.map(status => (
                          <SelectItem key={status} value={status}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Tax Settings - Only show in edit mode */}
              {isEditMode && (
                <div className='space-y-4'>
                  <div className='flex items-center justify-between'>
                    <h3 className='text-sm font-medium'>
                      Default Tax Settings
                    </h3>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => setTaxDialogOpen(true)}
                    >
                      <Percent className='mr-2 h-4 w-4' />
                      Manage Default Taxes
                    </Button>
                  </div>

                  <div className='rounded-lg border bg-muted/50 p-4'>
                    <p className='text-sm text-muted-foreground'>
                      Default taxes will apply to all customers in this
                      organization unless overridden by customer-specific tax
                      settings.
                    </p>
                  </div>
                </div>
              )}
            </form>
          </div>

          <DrawerFooter>
            <div className='flex gap-2 w-full'>
              <DrawerClose asChild>
                <Button
                  variant='outline'
                  onClick={handleCancel}
                  disabled={isSaving}
                  className='flex-1'
                >
                  <X className='w-4 h-4 mr-2' />
                  Cancel
                </Button>
              </DrawerClose>
              <Button
                onClick={form.handleSubmit(onSubmit)}
                disabled={isSaving}
                className='flex-1'
              >
                {isSaving && <Loader2 className='w-4 h-4 mr-2 animate-spin' />}
                <Save className='w-4 h-4 mr-2' />
                {isEditMode ? 'Update Organization' : 'Create Organization'}
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Tax Assignment Dialog */}
      {isEditMode && organization && (
        <TaxAssignmentDialog
          open={taxDialogOpen}
          onOpenChange={setTaxDialogOpen}
          entityType='organization'
          entityName={organization.name}
          currentTaxIds={[]} // TODO: Get from organization taxes
          onAssign={async _data => {
            // TODO: Call organizationsService.assignTaxes
          }}
        />
      )}
    </>
  )
}
